/**
 * 月次ふりかえり画面（§7.1・§10）＋肌年齢換算。
 *
 * 「浴びた量」をSED・プール換算に加えて **肌年齢が何歳ぶん進んだか** で表示する。
 * ただし換算係数は、店頭などで測った肌年齢の実測値から校正するまで暫定であり、
 * その旨を画面に出す（設計書§15の「確定前の値を精度の対外主張に使わない」に倣う）。
 */
import {
  calibrationNote,
  decomposeSkinAgeChange,
  describeSunburn,
  estimateSkinAgeYears,
  fitSkinAgeModel,
  formatSkinAgeYears,
  POOL_UNIT_NOTE,
  recommendSpf,
  summarizeMonth,
} from "@uvk/shared";
import type { DailyAggregate, ExposureSnapshot, SkinAgeReading } from "@uvk/shared";
import { db, listDayStates } from "../db.js";
import { currentUser, saveLocalPrefs, state, toAggregate } from "../state.js";
import { esc } from "../ui.js";

let readings: SkinAgeReading[] = [];
/** どのユーザーぶんを読み込み済みか。切り替えたら読み直す（ケア画面と同じ扱い） */
let readingsLoadedFor: string | null | undefined;

async function loadReadings(): Promise<void> {
  const uid = state.currentUserId;
  readings = (await db.getAll<SkinAgeReading>("skin_age"))
    .filter((r) => r.userId === uid)
    .sort((a, b) => a.ts - b.ts);
  readingsLoadedFor = uid;
}

/** 日次集計から、任意時点までの累積曝露を引ける関数を作る */
function exposureLookup(days: DailyAggregate[]): (ts: number) => ExposureSnapshot {
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
  return (ts: number) => {
    const limit = new Date(ts + 9 * 3600 * 1000).toISOString().slice(0, 10);
    let cumSed = 0;
    let cumPm25Day = 0;
    for (const d of sorted) {
      if (d.date > limit) break;
      cumSed += d.sedAct;
      cumPm25Day += d.pm25ExposureDay ?? 0;
    }
    return { ts, cumSed, cumPm25Day };
  };
}

export async function renderMonthly(el: HTMLElement): Promise<void> {
  if (readingsLoadedFor !== state.currentUserId) await loadReadings();
  const month = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 7);
  const all = await listDayStates();
  const today = toAggregate();
  const allDays: DailyAggregate[] = [...all.filter((r) => r.date !== today.date), today];
  const days = allDays.filter((r) => r.date.startsWith(month));

  const s = summarizeMonth(days);
  const rec = recommendSpf(days, state.settings);
  const unknownMin =
    all.filter((r) => r.date.startsWith(month)).reduce((a, r) => a + r.minUnknown, 0) +
    state.engine.minUnknown;

  const lookup = exposureLookup(allDays);
  const model = fitSkinAgeModel(readings, lookup);
  const monthExposure = {
    cumSed: s.sedAct,
    cumPm25Day: days.reduce((a, d) => a + (d.pm25ExposureDay ?? 0), 0),
  };
  const gained = estimateSkinAgeYears(model, monthExposure);
  const prevented = estimateSkinAgeYears(model, { cumSed: s.sedPrevented, cumPm25Day: 0 });

  // 実測の変化と比べる曝露は「実測の最初〜最後」の期間で取る。
  // 今月分と比べると期間が食い違い、差が意味を持たなくなる。
  const first = readings[0];
  const last = readings[readings.length - 1];
  const spanExposure =
    first && last && readings.length >= 2
      ? {
          cumSed: lookup(last.ts).cumSed - lookup(first.ts).cumSed,
          cumPm25Day: lookup(last.ts).cumPm25Day - lookup(first.ts).cumPm25Day,
        }
      : { cumSed: 0, cumPm25Day: 0 };
  const change = decomposeSkinAgeChange(model, readings, spanExposure);
  const spanLabel =
    first && last && readings.length >= 2
      ? `${new Date(first.ts).toLocaleDateString("ja-JP")}〜${new Date(last.ts).toLocaleDateString("ja-JP")}`
      : "";

  const burnAct = describeSunburn(s.sedAct);
  const burnPrevented = describeSunburn(s.sedPrevented);

  el.innerHTML = `
    <div class="card">
      <h2>🕰 ${month.replace("-", "年")}月の肌年齢 <span class="sub">${model.calibrated ? "校正済み" : "暫定"}</span></h2>
      <div class="row">
        <span class="big">${formatSkinAgeYears(gained)}</span>
        <span class="sub grow">紫外線と大気汚染で進んだぶん（推定）</span>
      </div>
      <div class="stat-grid" style="margin-top:8px">
        <div class="stat">
          <div class="sub">日焼け止めで守れたぶん</div>
          <div class="v">${formatSkinAgeYears(-prevented)}</div>
        </div>
        <div class="stat">
          <div class="sub">実測の変化${spanLabel ? `<br/><span style="font-size:10px">${spanLabel}</span>` : ""}</div>
          <div class="v">${change.measuredYears === null ? "—" : formatSkinAgeYears(change.measuredYears)}</div>
        </div>
      </div>
      ${
        change.careYears !== null
          ? `<p class="sub">同じ期間に浴びた量からの見込みは ${formatSkinAgeYears(change.exposureYears)}。
             実測との差は <b>${formatSkinAgeYears(change.careYears)}</b> で、
             ${
               Math.abs(change.careYears) < 0.05
                 ? "見込みどおりに進んでいます"
                 : change.careYears < 0
                   ? "ケアや生活が効いている可能性があります"
                   : "ケア以外の要因もありそうです"
             }
             （因果ではなく差の提示です）</p>`
          : `<p class="sub">実測を2回以上登録すると、実際の変化と見比べられます</p>`
      }
      <p class="sub">${esc(calibrationNote(model))}</p>
    </div>

    <div class="card">
      <h2>📏 肌年齢の実測を登録</h2>
      <p class="sub">店頭の肌年齢測定などの結果を入れると、あなたのデータで換算係数を校正します</p>
      <div class="row">
        <input type="number" id="measured-age" placeholder="肌年齢" min="5" max="99" step="0.1" style="width:100px" />
        <input type="number" id="chrono-age" placeholder="実年齢" min="5" max="99" value="${state.localPrefs.chronologicalAge ?? ""}" style="width:100px" />
        <input type="text" id="age-source" placeholder="測定元" value="店頭測定" class="grow" />
        <button id="add-reading" class="ghost">追加</button>
      </div>
      ${
        readings.length > 0
          ? readings
              .map(
                (r) => `<div class="row" style="font-size:13px;padding:4px 0;border-bottom:1px solid var(--line)">
                  <span class="grow">${new Date(r.ts).toLocaleDateString("ja-JP")}　肌年齢 ${r.measuredAge}歳（実年齢 ${r.chronologicalAge}歳）</span>
                  <span class="sub">${esc(r.source)}</span>
                  <button class="ghost" data-del-reading="${r.ts}">削除</button>
                </div>`,
              )
              .join("")
          : `<p class="sub">まだ登録がありません</p>`
      }
    </div>

    <div class="card">
      <h2>📊 浴びた量</h2>
      <div class="stat-grid">
        <div class="stat">
          <div class="sub">浴びた量</div>
          <div class="v">${burnAct.p}</div>
          <div class="sub">${burnAct.poolTime}／${burnAct.sed}</div>
        </div>
        <div class="stat">
          <div class="sub">防げた量</div>
          <div class="v">${burnPrevented.p}</div>
          <div class="sub">${burnPrevented.poolTime}／${burnPrevented.sed}</div>
        </div>
        <div class="stat"><div class="sub">カット率</div><div class="v">${Math.round(s.cutRate * 100)}%</div></div>
        <div class="stat">
          <div class="sub">PM2.5の曝露</div>
          <div class="v">${monthExposure.cumPm25Day.toFixed(0)}<small> μg/m³·日</small></div>
        </div>
      </div>
      ${unknownMin > 0 ? `<p class="sub">⚠️ 計測できなかった時間：${unknownMin}分（安全側で見積もっています）</p>` : ""}
      <p class="sub">${esc(POOL_UNIT_NOTE)}</p>
    </div>

    <div class="card">
      <h2>🎯 来月の目標</h2>
      <p><b>${rec.label} 以上</b>を使う ＋ <b>アラートを守る</b></p>
      <p class="sub">塗り忘れ日（屋外30分以上で塗布0回）：${s.forgottenDays}日 ／ 記録${s.daysCounted}日</p>
    </div>

    <p class="credit">Weather data by Open-Meteo.com (CC BY 4.0)</p>
  `;

  el.querySelector<HTMLButtonElement>("#add-reading")!.onclick = async () => {
    const measured = Number(el.querySelector<HTMLInputElement>("#measured-age")!.value);
    const chrono = Number(el.querySelector<HTMLInputElement>("#chrono-age")!.value);
    if (!Number.isFinite(measured) || !Number.isFinite(chrono) || measured <= 0 || chrono <= 0) {
      alert("肌年齢と実年齢を入れてください");
      return;
    }
    const user = currentUser();
    if (!user) {
      alert("肌年齢の記録はユーザーごとに残します。設定でユーザーを作ってください。");
      return;
    }
    const reading: SkinAgeReading = {
      ts: Date.now(),
      userId: user.id,
      measuredAge: measured,
      chronologicalAge: chrono,
      source: el.querySelector<HTMLInputElement>("#age-source")!.value.slice(0, 40) || "測定",
    };
    await db.put("skin_age", reading);
    readings.push(reading);
    readings.sort((a, b) => a.ts - b.ts);
    await saveLocalPrefs({ ...state.localPrefs, chronologicalAge: chrono });
    await renderMonthly(el);
  };

  el.querySelectorAll<HTMLButtonElement>("[data-del-reading]").forEach((b) => {
    b.onclick = async () => {
      const ts = Number(b.dataset.delReading);
      await db.delete("skin_age", ts);
      readings = readings.filter((r) => r.ts !== ts);
      await renderMonthly(el);
    };
  });
}
